(() => {
  if (!window.customElements || !window.fetch || !window.AbortController ||
      customElements.get('cm-collection-loader')) return;

  class CollectionLoader extends HTMLElement {
    connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      queueMicrotask(() => {
        if (!this.isConnected || this.generation !== generation) return;
        this.initialize();
      });
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.pause();
      this.mutations?.disconnect();
      document.removeEventListener('input', this.onFacetInput, true);
      document.removeEventListener('click', this.onFacetClick, true);
      window.removeEventListener('popstate', this.onHistory);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      this.button?.removeEventListener('click', this.onMore);
    }

    initialize() {
      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('ul#product-grid');
      this.button = this.querySelector('[data-cm-load-more]');
      this.status = this.querySelector('[data-cm-status]');
      this.sentinel = this.querySelector('[data-cm-sentinel]');
      if (!this.grid || !this.button || !this.status || !this.sentinel ||
          this.grid.dataset.id !== this.dataset.sectionId) return;
      this.pageParam = this.dataset.pageParam || 'page';
      this.contextURL = new URL(location.href);
      this.context = this.queryKey(this.contextURL);
      this.currentPage = Number(this.dataset.page);
      this.autoCount = Number(this.dataset.autoCount || 0);
      this.attempted = new Set();
      this.ids = new Set(Array.from(this.grid.children, (item) => item.dataset.cmProductId));
      this.suspended = false;
      this.failed = false;
      this.loading = false;
      this.onMore = () => {
        if (this.retryable) {
          this.failed = false;
          this.retryable = false;
        }
        this.load(false);
      };
      this.button.addEventListener('click', this.onMore);
      this.onFacetInput = (event) => {
        if (event.target.closest('facet-filters-form')) this.pause();
      };
      this.onFacetClick = (event) => {
        if (event.target.closest('facet-remove, .js-facet-remove')) this.pause();
      };
      this.onHistory = () => {
        if (this.queryKey(new URL(location.href)) !== this.context) this.pause();
      };
      this.onPageHide = () => this.pause();
      this.onPageShow = (event) => {
        if (event.persisted) {
          this.disconnectedCallback();
          this.connectedCallback();
        }
      };
      document.addEventListener('input', this.onFacetInput, true);
      document.addEventListener('click', this.onFacetClick, true);
      window.addEventListener('popstate', this.onHistory);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      this.mutations = new MutationObserver(() => {
        if (this.grid.closest('.collection')?.classList.contains('loading')) this.pause();
      });
      this.mutations.observe(this.grid.closest('.collection'), { attributes: true, attributeFilter: ['class'] });
      if (!Number.isInteger(this.currentPage) || this.currentPage < 1 ||
          this.ids.has(undefined) || this.ids.size !== this.grid.children.length) {
        this.fail();
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && this.autoCount < 3) this.load(true);
        }, { rootMargin: '0px 0px 500px 0px' });
      }
      this.refresh();
    }

    queryKey(url) {
      const params = new URLSearchParams(url.search);
      params.delete(this.pageParam);
      params.delete('section_id');
      params.delete('sections');
      // Shopify's editor session parameter is not emitted by paginate.next.url.
      // Ignore it for comparison only in server-confirmed design mode.
      if (this.dataset.designMode === 'true') params.delete('oseid');
      if (params.getAll('pb').length === 1 && params.get('pb') === '0') params.delete('pb');
      for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
        if (params.has(key) && params.getAll(key).every((value) => value === '')) params.delete(key);
      }
      return url.pathname + '?' + JSON.stringify(Array.from(params).sort());
    }

    nextURL(value) {
      if (!value) return null;
      const url = new URL(value, this.contextURL);
      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      if (url.origin !== location.origin || this.queryKey(url) !== this.context ||
          Number(url.searchParams.get(this.pageParam)) !== this.currentPage + 1) {
        throw new Error('Invalid pagination context');
      }
      if (this.dataset.designMode === 'true' && this.contextURL.searchParams.has('oseid')) {
        url.searchParams.set('oseid', this.contextURL.searchParams.get('oseid'));
      }
      return url;
    }

    relevant() {
      return this.isConnected && !this.suspended &&
        this.container.querySelector('#product-grid') === this.grid &&
        !this.grid.closest('.collection')?.classList.contains('loading') &&
        this.queryKey(new URL(location.href)) === this.context;
    }

    pause() {
      this.suspended = true;
      this.observer?.disconnect();
      this.controller?.abort();
      if (this.button) this.button.hidden = true;
      this.grid?.removeAttribute('aria-busy');
    }

    fail(retryable = false) {
      this.failed = true;
      this.retryable = retryable;
      this.observer?.disconnect();
      this.button.hidden = !retryable;
      this.button.disabled = false;
      this.status.textContent = this.dataset.errorMessage;
    }

    refresh() {
      this.observer?.disconnect();
      if (!this.relevant() || this.failed) return;
      this.button.disabled = false;
      this.button.hidden = !this.dataset.nextUrl;
      if (!this.dataset.nextUrl) {
        this.status.textContent = this.dataset.completeMessage;
        return;
      }
      try {
        this.nextURL(this.dataset.nextUrl);
      } catch {
        this.fail();
        return;
      }
      if (this.autoCount < 3) this.observer?.observe(this.sentinel);
    }

    async load(automatic) {
      if (this.loading || this.failed || !this.relevant() || !this.dataset.nextUrl ||
          (automatic && this.autoCount >= 3)) return;
      let url;
      try {
        url = this.nextURL(this.dataset.nextUrl);
        if (this.attempted.has(url.href)) throw new Error('Repeated page');
      } catch {
        this.fail();
        return;
      }
      this.loading = true;
      this.attempted.add(url.href);
      this.observer?.disconnect();
      this.button.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingMessage;
      const generation = this.generation;
      const controller = this.controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url.href, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok || (response.url && new URL(response.url).href !== url.href)) {
          throw new Error('Page unavailable');
        }
        const html = await response.text();
        if (generation !== this.generation || !this.relevant()) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const returnedContainer = parsed.getElementById('ProductGridContainer');
        const returnedGrid = returnedContainer?.querySelector('ul#product-grid');
        const returnedState = returnedContainer?.querySelector('cm-collection-loader');
        const page = Number(url.searchParams.get(this.pageParam));
        if (!returnedGrid || !returnedState || returnedGrid.dataset.id !== this.grid.dataset.id ||
            returnedState.dataset.sectionId !== this.dataset.sectionId ||
            returnedState.dataset.pageParam !== this.pageParam ||
            Number(returnedState.dataset.page) !== page) throw new Error('Unexpected page');
        const children = Array.from(returnedGrid.children);
        if (!children.length || children.some((item) => item.tagName !== 'LI' || !item.dataset.cmProductId)) {
          throw new Error('Missing product identity');
        }
        const newIds = new Set(this.ids);
        const items = children.filter((item) => {
          if (newIds.has(item.dataset.cmProductId)) return false;
          newIds.add(item.dataset.cmProductId);
          return true;
        });
        if (!items.length) throw new Error('No new products');
        const next = returnedState.dataset.nextUrl || '';
        if (next) {
          const following = new URL(next, url);
          if (following.origin !== location.origin || this.queryKey(following) !== this.context ||
              Number(following.searchParams.get(this.pageParam)) !== page + 1) {
            throw new Error('Non-progressing pagination');
          }
        }
        const fragment = document.createDocumentFragment();
        items.forEach((item) => {
          item.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])')
            .forEach((script) => script.remove());
          [item, ...item.querySelectorAll('.scroll-trigger')].forEach((element) => {
            if (element.classList.contains('scroll-trigger')) {
              element.classList.add('scroll-trigger--cancel');
              element.classList.remove('scroll-trigger--offscreen');
            }
          });
          fragment.append(item);
        });
        this.grid.append(fragment);
        this.ids = newIds;
        this.currentPage = page;
        this.dataset.page = String(page);
        this.dataset.nextUrl = next;
        if (automatic) this.dataset.autoCount = String(++this.autoCount);
        this.status.textContent = this.dataset.loadedMessage.replace('[count]', String(items.length));
        if (!automatic) {
          Array.from(items[0].querySelectorAll('a[href]'))
            .find((link) => link.getClientRects().length && getComputedStyle(link).visibility !== 'hidden')?.focus();
        }
        this.dispatchEvent(new CustomEvent('collection:products-appended', {
          bubbles: true, detail: { count: items.length },
        }));
      } catch {
        if (generation === this.generation && this.relevant()) {
          this.attempted.delete(url.href);
          this.fail(true);
        }
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation) {
          this.loading = false;
          this.grid.removeAttribute('aria-busy');
          this.refresh();
        }
      }
    }
  }

  customElements.define('cm-collection-loader', CollectionLoader);
})();
